/**
 * Booking Flow Analytics Events
 *
 * Tracking functions for the booking flow funnel on Tour Product Page.
 *
 * Event Data Flow:
 * 1. booking_started: Tour only (just context)
 * 2. date_selected: Tour + Date + Price
 * 3. pax_selected: Tour + Date + Price + Pax
 * 4. add_to_booking: Tour + Date + Price + Pax (complete)
 */

import { pushToDataLayer, pushToDataLayerDebounced } from '../core/dispatcher';
import type {
    AddToBookingPayload,
    BookingAnalyticsPayload,
    BookingStartedPayload,
    DateSelectedPayload,
    DateSelectionInput,
    PaxSelectedPayload,
    PaxSelectionInput,
} from '../core/types';

// =============================================================================
// Event Name Constants
// =============================================================================

const EVENT_BOOKING_STARTED = 'a.tour.booking_started' as const;
const EVENT_DATE_SELECTED = 'a.tour.date_selected' as const;
const EVENT_PAX_SELECTED = 'a.tour.pax_selected' as const;
const EVENT_BOOKING_ADDED = 'a.tour.booking_added' as const;

// =============================================================================
// Payload Builders
// =============================================================================

/**
 * Creates payload for booking_started event.
 * Only includes Tour and User (no selection).
 */
function createBookingStartedPayload(
    basePayload: BookingAnalyticsPayload,
): BookingStartedPayload {
    return {
        event: EVENT_BOOKING_STARTED,
        eventData: {
            tour: basePayload.eventData.tour,
            properties: basePayload.eventData.properties,
        },
    };
}

/**
 * Creates payload for date_selected event.
 * Includes Tour + Date + Price.
 */
function createDateSelectedPayload(
    basePayload: BookingAnalyticsPayload,
    selection: DateSelectionInput,
): DateSelectedPayload {
    return {
        event: EVENT_DATE_SELECTED,
        eventData: {
            tour: basePayload.eventData.tour,
            properties: basePayload.eventData.properties,
            selection: {
                start_date: selection.start_date,
                selected_price: selection.selected_price,
            },
        },
    };
}

/**
 * Creates payload for pax_selected event.
 * Includes Tour + Date + Price + Pax.
 */
function createPaxSelectedPayload(
    basePayload: BookingAnalyticsPayload,
    selection: PaxSelectionInput,
): PaxSelectedPayload {
    return {
        event: EVENT_PAX_SELECTED,
        eventData: {
            tour: basePayload.eventData.tour,
            properties: basePayload.eventData.properties,
            selection: {
                start_date: selection.start_date,
                selected_price: selection.selected_price,
                adults: selection.adults,
                children: selection.children,
            },
        },
    };
}

/**
 * Creates payload for add_to_booking event.
 * Includes complete booking data: Tour + Date + Price + Pax.
 */
function createAddToBookingPayload(
    basePayload: BookingAnalyticsPayload,
    selection: PaxSelectionInput,
): AddToBookingPayload {
    return {
        event: EVENT_BOOKING_ADDED,
        eventData: {
            tour: basePayload.eventData.tour,
            properties: basePayload.eventData.properties,
            selection: {
                start_date: selection.start_date,
                selected_price: selection.selected_price,
                adults: selection.adults,
                children: selection.children,
            },
        },
    };
}

// =============================================================================
// Public Tracking Functions
// =============================================================================

/**
 * Tracks booking_started event (modal open).
 * Only includes Tour context, no selection data.
 *
 * @param basePayload - The base analytics payload from PHP
 */
export function trackBookingStarted(
    basePayload: BookingAnalyticsPayload,
): void {
    const payload = createBookingStartedPayload(basePayload);
    pushToDataLayer(payload);
}

/**
 * Tracks date_selected event.
 * Includes Tour + Date + Price.
 *
 * @param basePayload - The base analytics payload from PHP
 * @param selection - The date selection input
 */
export function trackDateSelected(
    basePayload: BookingAnalyticsPayload,
    selection: DateSelectionInput,
): void {
    const payload = createDateSelectedPayload(basePayload, selection);
    pushToDataLayer(payload);
}

/**
 * Tracks pax_selected event with debouncing.
 * Includes Tour + Date + Price + Pax.
 * Uses debouncing to avoid event spam when user rapidly changes pax count.
 *
 * @param basePayload - The base analytics payload from PHP
 * @param selection - The pax selection input
 */
export function trackPaxSelectedDebounced(
    basePayload: BookingAnalyticsPayload,
    selection: PaxSelectionInput,
): void {
    const payload = createPaxSelectedPayload(basePayload, selection);
    pushToDataLayerDebounced(payload);
}

/**
 * Tracks add_to_booking event (form submission).
 * Includes complete booking data: Tour + Date + Price + Pax.
 *
 * @param basePayload - The base analytics payload from PHP
 * @param selection - The complete selection input
 */
export function trackAddToBooking(
    basePayload: BookingAnalyticsPayload,
    selection: PaxSelectionInput,
): void {
    const payload = createAddToBookingPayload(basePayload, selection);
    pushToDataLayer(payload);
}

// =============================================================================
// Type Re-exports for Consumer Convenience
// =============================================================================

export type {
    BookingAnalyticsPayload,
    DateSelectionInput,
    PaxSelectionInput,
} from '../core/types';
